import { defineComponent as _defineComponent } from 'vue'
import { cloneDeep } from 'lodash';
import { now } from '@/utils/datetime';
import { onMounted, onUpdated, PropType, reactive, ref, computed } from 'vue';

import {
  SdsProduct,
  SdsSection15,
  SdsSection15Cas,
  SdsSection15Material,
  SdsSection3Material,
  SdsSection3ImpurityMaterial,
} from '@/interfaces/model/general/sds';
import { NewSdsSection15 } from '@/lib/constructor/model/general/sds';
import { LawId, RegulationId } from '@/lib/enum';
import SdsSection15MaterialForm from '@/views/dashboard/sdsDrawer/forms/SdsSection15MaterialForm.vue';
import { VForm } from 'vuetify/lib/components';

import { readUserLocale } from '@/store/main/getters';
import { store } from '@/store/index';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useEnumValidation } from '@/composables/useEnumValidation';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection15Material',
  props: {
  isEditing: {
    type: Boolean,
    default: false,
  },
  section15: {
    type: Array as PropType<Array<SdsSection15>>,
    required: true,
    default: () => [],
  },
  productIndex: {
    type: Number,
    default: 0,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
  },
  section3MaterialList: {
    type: Array as PropType<
      Array<SdsSection3Material | SdsSection3ImpurityMaterial>
    >,
    default: () => [],
  },
  sdsId: {
    type: Number,
    default: 0,
  },
},
  emits: ["update:isValid", "update:section15"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:section15', data: Array<SdsSection15>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);
const locale = computed(() => readUserLocale(store));
const textProperty = locale.value === 'ja-JP' ? 'textJp' : 'textEn';

// Use shared enum validation functions
const { lawExists, regulationExists } = useEnumValidation();



const materialNewFormRef = ref(null);
const materialUpdateFormRef = ref(null);

const getMaterials = computed(() => {
  if (updatedSection15.material_list.length > 0) {
    const material = updatedSection15.material_list[0];

    // Find the material in section3MaterialList to preserve the is_impurity flag
    const materialFromSection3 = props.section3MaterialList.find(
      (m) => m.material_name === material.material_name
    );

    // Create a new array with filtered section3MaterialList
    const filteredMaterialList = props.section3MaterialList.filter(
      (m) => m.material_name !== material.material_name
    );

    // Return the current material from section3 first (preserves is_impurity flag), then the filtered list
    // If not found in section3 (shouldn't happen), fall back to the material from section15
    const currentMaterial = materialFromSection3 || material;
    return [currentMaterial, ...filteredMaterialList];
  } else {
    return [...props.section3MaterialList];
  }
});

// Flatten section15 so each row is a material from a section15
const section15MaterialRows = computed(() => {
  const rows = [];
  for (const s15 of props.section15) {
    if (Array.isArray(s15.material_list) && s15.material_list.length > 0) {
      for (const mat of s15.material_list) {
        rows.push({
          ...s15,
          material_name: mat.material_name || '',
          material: mat,
          cas_numbers: (mat.cas_list || [])
            .map((cas) => cas.cas_number)
            .join(', '),
        });
      }
    }
  }
  return rows;
});

const isValidUpdateForm = ref<boolean>(false);
const isValidAddForm = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>(null);
const newSdsSection15Form = ref();
const expanded = ref<Array<number>>([0]);
const modalDeleteSection15 = ref<boolean>(false);
const modalRescueSection15 = ref<boolean>(false);
const modalEditSection15 = ref<boolean>(false);
const modalAddSection15 = ref<boolean>(false);

const newSection15 = reactive({ ...NewSdsSection15 });
const updatedSection15 = reactive({ ...NewSdsSection15 });

const section15Index = ref<number>(0);



const parseSection15 = (section15: SdsSection15): void => {
  updatedSection15.applicable_id = section15.applicable_id;
  updatedSection15.law_id = section15.law_id;
  updatedSection15.regulation1_id = section15.regulation1_id;
  updatedSection15.regulation2_id = section15.regulation2_id;
  updatedSection15.regulation3_id = section15.regulation3_id;
  updatedSection15.regulation4_id = section15.regulation4_id;
  updatedSection15.regulation5_id = section15.regulation5_id;
  updatedSection15.text = section15.text;
  updatedSection15.material_list = section15.material_list;
};

const editSection15 = () => {
  if (materialUpdateFormRef.value) {
    materialUpdateFormRef.value.syncMaterials();

    const data = [...cloneDeep(props.section15)];
    data[section15Index.value].applicable_id = updatedSection15.applicable_id;
    data[section15Index.value].law_id = updatedSection15.law_id;
    data[section15Index.value].regulation1_id = updatedSection15.regulation1_id;
    data[section15Index.value].regulation2_id = updatedSection15.regulation2_id;
    data[section15Index.value].regulation3_id = updatedSection15.regulation3_id;
    data[section15Index.value].regulation4_id = updatedSection15.regulation4_id;
    data[section15Index.value].regulation5_id = updatedSection15.regulation5_id;
    data[section15Index.value].text = updatedSection15.text;
    data[section15Index.value].material_list = structuredClone(
      updatedSection15.material_list
    );
    emits('update:section15', data);
  }
};

const addSection15 = () => {
  if (materialNewFormRef.value) {
    materialNewFormRef.value.syncMaterials();

    const data: Array<SdsSection15> = [...props.section15];
    data.push({
      ...structuredClone(newSection15),
      sds_id: props.products[props.productIndex].sds_id,
      sds_product_id: props.products[props.productIndex].id,
      law_id: newSection15.law_id,
      regulation1_id: newSection15.regulation1_id,
      regulation2_id: newSection15.regulation2_id,
      regulation3_id: newSection15.regulation3_id,
      regulation4_id: newSection15.regulation4_id,
      regulation5_id: newSection15.regulation5_id,
      text: newSection15.text,
      applicable_id: newSection15.applicable_id,
      created_at: now().toString(),
      updated_at: now().toString(),
      deleted_at: null,
      material_list: structuredClone(newSection15.material_list),
    });

    emits('update:section15', data);
  }
};

const clearNewSection15Form = () => {
  newSection15.applicable_id = 2;
  newSection15.law_id = 0;
  newSection15.regulation1_id = 0;
  newSection15.regulation2_id = 0;
  newSection15.regulation3_id = 0;
  newSection15.regulation4_id = 0;
  newSection15.regulation5_id = 0;
  newSection15.text = '';
  newSection15.material_list.splice(0);
  newSection15.deleted_at = null;
};

const deleteSection15Data = (): void => {
  const data = props.section15;
  data[section15Index.value].deleted_at = now().toString();
  data[section15Index.value].material_list.forEach(
    (material: SdsSection15Material) => {
      material.deleted_at = now().toString();
      material.cas_list.forEach((cas: SdsSection15Cas) => {
        cas.deleted_at = now().toString();
      });
    }
  );

  emits('update:section15', data);
};

const rescueSection15Data = (): void => {
  const data = props.section15;
  data[section15Index.value].deleted_at = null;
  data[section15Index.value].material_list.forEach(
    (material: SdsSection15Material) => {
      material.deleted_at = null;
      material.cas_list.forEach((cas: SdsSection15Cas) => {
        cas.deleted_at = null;
      });
    }
  );

  emits('update:section15', data);
};

const openAddSection15Modal = (): void => {
  modalAddSection15.value = true;
};

const closeAddSection15Modal = (): void => {
  modalAddSection15.value = false;
};

const openEditSection15Modal = (): void => {
  modalEditSection15.value = true;
};

const closeEditSection15Modal = (): void => {
  modalEditSection15.value = false;
};

const openDeleteSection15Modal = (): void => {
  modalDeleteSection15.value = true;
};

const closeDeleteSection15Modal = (): void => {
  modalDeleteSection15.value = false;
};

const openRescueSection15Modal = (): void => {
  modalRescueSection15.value = true;
};

const closeRescueSection15Modal = (): void => {
  modalRescueSection15.value = false;
};

onMounted((): void => {
  if (form.value) {
    form.value.validate();
  }
});

onUpdated((): void => {
  newSdsSection15Form.value;
});

return { __sfc: true,$t, locale, textProperty, lawExists, regulationExists, props, materialNewFormRef, materialUpdateFormRef, getMaterials, section15MaterialRows, isValidUpdateForm, isValidAddForm, form, newSdsSection15Form, expanded, modalDeleteSection15, modalRescueSection15, modalEditSection15, modalAddSection15, newSection15, updatedSection15, section15Index, emits, parseSection15, editSection15, addSection15, clearNewSection15Form, deleteSection15Data, rescueSection15Data, openAddSection15Modal, closeAddSection15Modal, openEditSection15Modal, closeEditSection15Modal, openDeleteSection15Modal, closeDeleteSection15Modal, openRescueSection15Modal, closeRescueSection15Modal, LawId, RegulationId, SdsSection15MaterialForm, VForm, sharedMessages }
}

})